import { Typo } from '@/components/ui/typography';
import { useElementSize } from '@/hooks/use-element-size';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { CSSProperties, useMemo } from 'react';

export interface CheckoutStep {
  id: 'services' | 'cart' | 'checkout';
  title: string;
  route: string;
  completed: boolean;
  current: boolean;
}

interface CheckoutProgressProps {
  steps: CheckoutStep[];
  className?: string;
}

export function CheckoutProgress({ steps, className }: CheckoutProgressProps) {
  const [setRef, size] = useElementSize<HTMLDivElement>();

  const beforeWidth = useMemo(() => {
    return `calc(${(size.width ?? 0) - 64 - 210}px/2)`;
  }, [size.width]);

  return (
    <div className={cn('w-full rounded-2xl bg-white px-4 py-6 shadow md:px-8', className)}>
      <div className="cart-steps flex items-center justify-between" ref={setRef}>
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={cn(
              'cart-step-item relative flex flex-col items-center justify-center bg-white',
              'not-last:before:w-[var(--before-width)]',
              steps[index]?.current ? 'completed' : '',
            )}
            style={
              {
                '--before-width': beforeWidth,
              } as CSSProperties
            }
          >
            <div
              className={cn(
                'relative flex size-12 items-center justify-center rounded-full border-2 transition-colors md:size-16',
                step.completed
                  ? 'border-primary bg-primary text-white'
                  : step.current
                    ? 'border-primary text-primary bg-white'
                    : 'border-gray-300 bg-gray-100 text-gray-500',
              )}
            >
              {step.completed ? <Check className="size-5 md:size-6" /> : <span className="text-lg font-semibold md:text-2xl">{index + 1}</span>}
            </div>

            {/* Step Title */}
            <Typo variant="small" className={cn('mt-2 text-center font-medium', step.current ? 'text-primary' : 'text-gray-600')}>
              {step.title}
            </Typo>
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper function to generate checkout steps
export function generateCheckoutSteps(currentStep: 'services' | 'cart' | 'checkout'): CheckoutStep[] {
  const stepOrder: ('services' | 'cart' | 'checkout')[] = ['services', 'cart', 'checkout'];
  const currentIndex = stepOrder.indexOf(currentStep);

  return [
    {
      id: 'services',
      title: 'Chọn dịch vụ',
      route: '/search',
      completed: currentIndex > 0,
      current: currentIndex === 0,
    },
    {
      id: 'cart',
      title: 'Dịch vụ bổ sung',
      route: '/cart',
      completed: currentIndex > 1,
      current: currentIndex === 1,
    },
    {
      id: 'checkout',
      title: 'Thanh toán',
      route: '/checkout',
      completed: currentIndex > 2,
      current: currentIndex === 2,
    },
  ];
}
